import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { ScannerStats } from "@/components/producer/scanner/ScannerStats";
import { QRScanner } from "@/components/producer/scanner/QRScanner";
import { ManualSearch } from "@/components/producer/scanner/ManualSearch";
import { ScanResult } from "@/components/producer/scanner/ScanResult";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SEO } from "@/components/SEO";

interface Event {
  id: string;
  title: string;
  date: string;
  currency: string;
}

export interface OrderWithCheckins {
  id: string;
  reference: string;
  user_first_name: string;
  user_last_name: string;
  user_email: string;
  vip_ticket_quantity: number | null;
  standing_tickets: number | null;
  early_bird_tickets: number | null;
  status: string;
  total_tickets: number;
  checked_in_count: number;
}

export default function ProducerScanner() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get("eventId");
  
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState<Event | null>(null);
  const [scanResult, setScanResult] = useState<OrderWithCheckins | null>(null);
  const [scanStatus, setScanStatus] = useState<'idle' | 'valid' | 'invalid' | 'exhausted'>('idle');
  const [totalTickets, setTotalTickets] = useState(0);
  const [checkedInCount, setCheckedInCount] = useState(0);
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    checkAuthAndLoadEvent();
  }, [eventId]);

  const checkAuthAndLoadEvent = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/producers/login");
        return;
      }
      setUser(user);

      // Check producer role
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);

      const hasProducerRole = roles?.some(r => r.role === "producer" || r.role === "admin");
      if (!hasProducerRole) {
        navigate("/producers/login");
        return;
      }

      if (!eventId) {
        navigate("/producers/dashboard");
        return;
      }

      // Load event
      const { data: eventData, error: eventError } = await supabase
        .from("events")
        .select("id, title, date, currency")
        .eq("id", eventId)
        .eq("producer_id", user.id)
        .single();

      if (eventError || !eventData) {
        console.error("Event not found or access denied");
        navigate("/producers/dashboard");
        return;
      }

      setEvent(eventData);
      await loadStats(eventId, user.id);
    } catch (error) {
      console.error("Error loading scanner:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async (eventId: string, producerId: string) => {
    console.log("Loading stats for event:", eventId, "producer:", producerId);
    
    // Use secure RPC function to get orders (bypasses RLS)
    const { data: orders, error: ordersError } = await supabase
      .rpc('get_producer_event_orders', {
        p_event_id: eventId,
        p_producer_id: producerId
      });

    console.log("Orders query result:", { orders, ordersError });

    // Filter for completed/paid and calculate total
    const completedOrders = (orders || []).filter(
      (o: any) => o.status === 'completed' || o.status === 'paid'
    );
    
    const total = completedOrders.reduce((sum: number, o: any) => {
      return sum + (o.vip_ticket_quantity || 0) + (o.standing_tickets || 0) + (o.early_bird_tickets || 0);
    }, 0);
    
    console.log("Total tickets calculated:", total);
    setTotalTickets(total);

    // Get checked in count
    const { data: checkins, error: checkinsError } = await supabase
      .from("ticket_checkins")
      .select("checked_in_count")
      .eq("event_id", eventId);

    console.log("Checkins query result:", { checkins, checkinsError });

    const checkedIn = checkins?.reduce((sum, c) => sum + c.checked_in_count, 0) || 0;
    setCheckedInCount(checkedIn);
  };

  const handleScan = async (reference: string) => {
    if (!eventId || !user) return;

    setScanStatus('idle');
    setScanResult(null);

    // Use secure RPC function to get orders
    const { data: orders, error } = await supabase
      .rpc('get_producer_event_orders', {
        p_event_id: eventId,
        p_producer_id: user.id
      });

    if (error) {
      console.error("Error fetching orders:", error);
      setScanStatus('invalid');
      return;
    }

    // Find the order by reference
    const order = (orders || []).find(
      (o: any) => o.reference.toUpperCase() === reference.toUpperCase()
    );

    if (!order) {
      setScanStatus('invalid');
      return;
    }

    // Check if order is completed
    if (order.status !== "completed" && order.status !== "paid") {
      setScanStatus('invalid');
      return;
    }

    // Calculate total tickets for this order
    const totalOrderTickets = (order.vip_ticket_quantity || 0) + 
                              (order.standing_tickets || 0) + 
                              (order.early_bird_tickets || 0);

    // Get existing check-ins for this order
    const { data: checkins } = await supabase
      .from("ticket_checkins")
      .select("checked_in_count")
      .eq("order_id", order.id);

    const alreadyCheckedIn = checkins?.reduce((sum, c) => sum + c.checked_in_count, 0) || 0;

    const orderWithCheckins: OrderWithCheckins = {
      ...order,
      total_tickets: totalOrderTickets,
      checked_in_count: alreadyCheckedIn,
    };

    setScanResult(orderWithCheckins);

    if (alreadyCheckedIn >= totalOrderTickets) {
      setScanStatus('exhausted');
    } else {
      setScanStatus('valid');
    }
  };

  const handleCheckIn = async (count: number) => {
    if (!scanResult || !eventId || !user) return;

    const { error } = await supabase
      .from("ticket_checkins")
      .insert({
        order_id: scanResult.id,
        event_id: eventId,
        checked_in_count: count,
        checked_in_by: user.id,
      });

    if (!error) {
      await loadStats(eventId, user.id);
      setScanResult({
        ...scanResult,
        checked_in_count: scanResult.checked_in_count + count,
      });
      
      if (scanResult.checked_in_count + count >= scanResult.total_tickets) {
        setScanStatus('exhausted');
      }
    }
  };

  const resetScan = () => {
    setScanResult(null);
    setScanStatus('idle');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title={`Scanner - ${event?.title || 'Event'} | SLAY`}
        description="Scan and validate attendee tickets"
      />
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-6 max-w-2xl">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/producers/dashboard")}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-foreground">{event?.title}</h1>
              <p className="text-sm text-muted-foreground">Ticket Scanner</p>
            </div>
          </div>

          {/* Stats */}
          <ScannerStats 
            checkedIn={checkedInCount} 
            total={totalTickets} 
          />

          {/* Scanner Tabs */}
          <Tabs defaultValue="scan" className="mt-6">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="scan">Scan QR</TabsTrigger>
              <TabsTrigger value="search">Manual Search</TabsTrigger>
            </TabsList>
            
            <TabsContent value="scan" className="mt-4">
              {scanStatus === 'idle' ? (
                <QRScanner onScan={handleScan} />
              ) : (
                <ScanResult 
                  result={scanResult}
                  status={scanStatus}
                  onCheckIn={handleCheckIn}
                  onReset={resetScan}
                />
              )}
            </TabsContent>
            
            <TabsContent value="search" className="mt-4">
              <ManualSearch 
                eventId={eventId!}
                producerId={user?.id || ''}
                onSelectOrder={(order) => {
                  setScanResult(order);
                  if (order.checked_in_count >= order.total_tickets) {
                    setScanStatus('exhausted');
                  } else {
                    setScanStatus('valid');
                  }
                }}
              />
              {scanResult && scanStatus !== 'idle' && (
                <div className="mt-4">
                  <ScanResult 
                    result={scanResult}
                    status={scanStatus}
                    onCheckIn={handleCheckIn}
                    onReset={resetScan}
                  />
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </>
  );
}
